/**
 * Switch <uo-switch>
 *
 * Attributes:
 * label
 * helper
 * checked
 * required
 * id
 * status-label
 * disabled
 */

// Import utilities
import { isEmpty, isDefined } from '@automator/utilities';

// Import base class and style
import InputBase from '../base';

// Import style for web component
const styles = require( './web-component-style.scss' );

/**
 * Creates <uo-switch>
 */
class AutomatorSwitch extends InputBase( HTMLElement ) {
	/**
	 * Returns the component attributes
	 * We don't use observedAttributes here because we want to add data to the attributes
	 *
	 * @return {Object} The attributes
	 */
	static get componentAttributes() {
		// Extend base observed attributes
		return Object.assign( this.baseComponentAttributes, {
			checked: {
				type: 'boolean',
			},
			'status-label': {
				type: 'string',
				propertyKey: 'statusLabel',
			},
		} );
	}

	/**
	 * Creates the component
	 *
	 * @return {undefined}
	 */
	constructor() {
		// Invoke constructor of parent class
		super();

		// Add styles
		this.#addStyles();

		// Create control
		this.#createControl();
	}

	/**
	 * Callback invoked when the component is added to the DOM
	 *
	 * @return {undefined}
	 */
	connectedCallback() {
		// Listen changes to the input value
		this.#listenInputChanges();
	}

	/**
	 * Add styles specific to this input
	 *
	 * @return {undefined}
	 */
	#addStyles() {
		// Insert CSS
		this.shadowRoot.querySelector( 'style' ).insertAdjacentHTML( 'beforeEnd', styles );
	}

	/**
	 * Creates the control
	 *
	 * @return {undefined}
	 */
	#createControl() {
		// The control wrapper
		this.$controlWrapper.insertAdjacentHTML(
			'beforeEnd',
			`
            <label class="switch">
                <input type="checkbox">
                <span class="switch__handle"></span>
            </label>
        `
		);
	}

	/**
	 * Returns the value (for forms)
	 *
	 * @return {undefined}
	 */
	get value() {
		// Gets the field value
		return this.checked ? '1' : '0';
	}

	/**
	 * Listen changes to the input value
	 *
	 * @return {undefined}
	 */
	#listenInputChanges() {
		// Listen the change event on the inner field
		this.$input.addEventListener( 'change', () => {
			// Trigger change event on the web component
			this.dispatchEvent(
				new CustomEvent( 'change', {
					detail: {
						context: 'user-input',
					},
				} )
			);
		} );
	}

	/**
	 * Returns the status label
	 * This is a label added next to the switch, that changes based on the value
	 *
	 * @return {String} The status label
	 */
	get statusLabel() {
		return this.getAttribute( 'status-label' );
	}

	/**
	 * Sets the status label
	 * This is a label added next to the switch, that changes based on the value
	 *
	 * @param {String} label The label
	 * @return {undefined}
	 */
	set statusLabel( label ) {
		if ( ! isEmpty( label ) ) {
			// Set status label in the host
			this.setAttribute( 'status-label', label );

			// Creates a status label in the element
			this.#createLabelElement( label );
		} else {
			// Set status label in the host
			this.removeAttribute( 'status-label' );

			// Remove the status label from the field
			this.#removeLabelElement();
		}
	}

	/**
	 * Creates the status label in the DOM
	 *
	 * @param {String} label The label
	 * @return {undefined}
	 */
	#createLabelElement( label ) {
		// Get the label(s)
		const statusLabel = label.split( ',' );

		// Check if there is more than one status
		const statusLabelIsDynamic = statusLabel.length == 2;

		// Remove the previous label
		this.#removeLabelElement();

		// Create the element
		const $label = document.createElement( 'span' );
		$label.classList.add( 'switch__label' );

		// Check if the label is dynamic
		if ( statusLabelIsDynamic ) {
			$label.insertAdjacentHTML(
				'beforeEnd',
				`
                <span class="switch__label-dynamic switch__label-dynamic--on">${statusLabel[0]}</span>
                <span class="switch__label-dynamic switch__label-dynamic--off">${statusLabel[1]}</span>
            `
			);
		} else {
			$label.insertAdjacentHTML( 'beforeEnd', statusLabel );
		}

		// Creates a label in the element
		this.$switch.insertAdjacentElement( 'beforeEnd', $label );
	}

	/**
	 * Removes the status label from the DOM
	 *
	 * @return {undefined}
	 */
	#removeLabelElement() {
		// Check if there is a label
		if ( isDefined( this.$statusLabel ) ) {
			// Remove element
			this.$statusLabel.parentNode.removeChild( this.$statusLabel );
		}
	}

	/**
	 * Returns the checked status of the switch
	 *
	 * @return {Boolean} True if it's checked
	 */
	get checked() {
		return this.$input.checked;
	}

	/**
	 * Sets whether it's checked
	 *
	 * @param {Boolean} value TRUE if it's checked
	 * @return {undefined}
	 */
	set checked( value ) {
		if ( value ) {
			// Set checked status in the host
			this.setAttribute( 'checked', '' );

			// Set the attribute in the input
			this.$input.setAttribute( 'checked', '' );
		} else {
			// Set checked status in the host
			this.removeAttribute( 'checked' );

			// Set the attribute in the input
			this.$input.removeAttribute( 'checked' );
		}

		// Dispatch change event
		this.dispatchEvent(
			new CustomEvent( 'change', {
				detail: {
					context: 'set-value',
				},
			} )
		);
	}

	/**
	 * Override (also defined in InputBase)
	 * Checks whether the input connected to the label is required
	 *
	 * @return {Boolean} True if it's required
	 */
	get required() {
		return this.hasAttribute( 'required' );
	}

	/**
	 * Override (also defined in InputBase)
	 * Sets whether the input connected to the label is required
	 *
	 * @param {Boolean} value TRUE if the input connected to the label is required
	 * @return {undefined}
	 */
	set required( value ) {
		if ( value ) {
			// Set required status in the host
			this.setAttribute( 'required', '' );

			// Set the attribute in the input
			this.$input.setAttribute( 'required', '' );
		} else {
			// Set required status in the host
			this.removeAttribute( 'required' );

			// Set the attribute in the input
			this.$input.removeAttribute( 'required' );
		}
	}

	/**
	 * Override (also defined in InputBase)
	 * Returns the id
	 *
	 * @return {String} The id
	 */
	get id() {
		return this.getAttribute( 'id' );
	}

	/**
	 * Override (also defined in InputBase)
	 * Sets the id
	 *
	 * @param {String} id The id
	 * @return {undefined}
	 */
	set id( value ) {
		if ( value ) {
			// Set id in the host
			this.setAttribute( 'id', value );
		} else {
			// Set id in the host
			this.removeAttribute( 'id' );
		}
	}

	/**
	 * Get the switch element
	 *
	 * @return {Node} The switch element
	 */
	get $switch() {
		return this.shadowRoot.querySelector( '.switch' );
	}

	/**
	 * Get the status label element
	 *
	 * @return {Node} The status label element
	 */
	get $statusLabel() {
		return this.shadowRoot.querySelector( '.switch__label' );
	}

	/**
	 * Get the input element
	 *
	 * @return {Node} The input element
	 */
	get $input() {
		return this.shadowRoot.querySelector( 'input' );
	}
}

customElements.define( 'uo-switch', AutomatorSwitch );
